// subscriptions.js
// Contains all HTTP requests related to subscription logic
import axios from 'axios';

// Get API base URL from environment variables or use a default
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with default config
const api = axios.create({
  baseURL: BASE_URL + '/api/subscriptions',
});

// Request interceptor to attach the auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Fetch all available plans
 */
export const getAvailablePlans = async () => {
  try {
    const response = await api.get('/plans/');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch plans');
  }
};

/**
 * Fetch active plans only
 */
export const getActivePlans = async () => {
  try {
    const response = await api.get('/plans/active/');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch active plans');
  }
};

/**
 * Get details of a specific plan
 */
export const getPlanDetails = async (planId) => {
  try {
    const response = await api.get(`/plans/${planId}/`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch plan details');
  }
};

/**
 * Fetch plans by type (free, trial, premium)
 */
export const getPlansByType = async (type) => {
  try {
    const response = await api.get(`/plans/by_type/?type=${type}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch plans by type');
  }
};

/**
 * Compare multiple plans  */

export const comparePlans = async (planIds) => {
  try {
    const idsParam = planIds.join(',');
    const response = await api.get(`/plans/compare/?ids=${idsParam}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to compare plans');
  }
}; 

/**
 * Get current user subscriptions
 */
export const getCurrentSubscription = async () => {
  try {
    const response = await api.get('/subscriptions/active/');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription');
  }
};

/**
 * Get all subscriptions for the user
 */
export const getAllUserSubscriptions = async () => {
  try {
    const response = await api.get('/subscriptions/');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscriptions');
  }
};

/**
 * Get details of a specific subscription
 */
export const getSubscriptionDetails = async (subscriptionId) => {
  try {
    const response = await api.get(`/subscriptions/${subscriptionId}/`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription details');
  }
};

/**
 * Subscribe to a plan
 */
export const subscribeToPlan = async (planId, couponCode = null) => {
  try {
    // Create the order
    const createOrderBody = { plan_id: planId };
    if (couponCode) {
      createOrderBody.coupon_code = couponCode;
    }
    
    const orderResponse = await api.post('/payment/create-order/', createOrderBody);
    return orderResponse.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to subscribe to plan');
  }
};

/**
 * Verify payment after Razorpay checkout
 */
export const verifyPayment = async (paymentData) => {
  // console.data('Payment data:', paymentData);
  try {
    const response = await api.post('/payment/verify/', paymentData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to verify payment');
  }
};

/**
 * Validate a coupon code
 */
export const validateCoupon = async (couponCode, planId) => {
  try {
    const response = await api.post('/coupons/validate/', {
      coupon_code: couponCode,
      plan_id: planId
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Invalid coupon code');
  }
};

/**
 * Cancel a subscription
 */
export const cancelSubscription = async (subscriptionId) => {
  try {
    const response = await api.post(`/subscriptions/${subscriptionId}/cancel/`, {});
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to cancel subscription');
  }
};

/**
 * Renew a subscription
 */
export const renewSubscription = async (subscriptionId) => {
  try {
    const response = await api.post(`/subscriptions/${subscriptionId}/renew/`, {});
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to renew subscription');
  }
};

/**
 * Request a refund for a subscription
 */
export const requestRefund = async (subscriptionId, reason) => {
  try {
    const response = await api.post(`/subscriptions/${subscriptionId}/request_refund/`, {
      reason: reason
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to request refund');
  }
};

/**
 * Get user subscription status (dashboard)
 */
export const getSubscriptionStatus = async () => {
  try {
    const response = await api.get('/dashboard/status/');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription status');
  }
};

/**
 * Get user subscription history
 */
export const getSubscriptionHistory = async () => {
  try {
    const response = await api.get('/user/subscription-history/');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription history');
  }
};

// eslint-disable-next-line
export default {
  getAvailablePlans,
  getActivePlans,
  getPlanDetails,
  getPlansByType,
  comparePlans,
  getCurrentSubscription,
  getAllUserSubscriptions,
  getSubscriptionDetails,
  subscribeToPlan,
  verifyPayment,
  validateCoupon,
  cancelSubscription,
  renewSubscription,
  requestRefund,
  getSubscriptionStatus,
  getSubscriptionHistory
};